import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { Configuration, PublicClientApplication } from '@azure/msal-browser'
import { queryClient } from "./lib/queryClient";
import { routeTree } from "./routeTree.gen";
import { ErrorProvider, useError } from "./context/ErrorContext";
import { httpClient, HttpStatusCode } from "./lib/httpClient";
import { CommonRoute } from "./constants/routes";
import { UserAccessControlProvider, useUserAccessControl } from "./context/UserAccessControl";
import { MasterTableProvider, useMasterTable } from "./context/MasterTable";
import { UserAuthUtil } from "./utils/userAuth";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ID_REQUEST_NAME_STORAGE, LOCAL_STORAGE } from "./constants/commons";
import type { HttpError } from "./interfaces/http-client";
import { getIdRequest } from "./utils/commoms";

const router = createRouter({ routeTree, context: { queryClient } });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_APP_IAM_CLIENT_ID,
    authority: import.meta.env.VITE_APP_IAM_AUTHORITY,
    redirectUri: import.meta.env.VITE_APP_IAM_REDIRECT_URI,
    postLogoutRedirectUri: import.meta.env.VITE_APP_IAM_REDIRECT_URI
  },
  cache: {
    cacheLocation: LOCAL_STORAGE
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

const CRITICAL_ERROR_CODES: readonly number[] = [
  HttpStatusCode.SERVER_ERROR,
  HttpStatusCode.UNAVAILABLE_SERVICE,
  HttpStatusCode.NOT_FOUND,
  HttpStatusCode.BAD_GATEWAY,
  HttpStatusCode.UNAUTHORIZED,
];

const ERROR_MESSAGE = 'El servicio ha devuelto un error. Inténtalo más tarde o comunícate con el administrador.';

function AppWithErrorHandler() {
  const { isAuthenticated} = useAuth();
  const { showNotification } = useError();
  const {loadTableMasterAndParameterData} = useMasterTable();
  const { initUserAccessControlData } = useUserAccessControl();

  useEffect(() => {
    const handleHttpError = (error: HttpError): void => {
      if (shouldRedirectToRetry(error, isAuthenticated)) {
        window.location.replace(CommonRoute.RETRY);
        return;
      }

      if (isCriticalError(error.statusCode)) {
        showNotification('warning', ERROR_MESSAGE, 5000);
      }
    };

    httpClient.setErrorHandler(handleHttpError);
    if(UserAuthUtil.getAccessToken()){ 
      const idRequest = getIdRequest();
      sessionStorage.setItem(ID_REQUEST_NAME_STORAGE, String(idRequest));
      loadTableMasterAndParameterData?.();
      initUserAccessControlData();
    }

  }, [isAuthenticated, showNotification]);

  return <RouterProvider router={router} />;
}

function shouldRedirectToRetry(error: HttpError, isAuth: boolean): boolean {
  return (
    isAuth &&
    error.statusCode === HttpStatusCode.UNAUTHORIZED &&
    window.location.pathname !== CommonRoute.RETRY
  );
}

function isCriticalError(statusCode: number): boolean {
  return CRITICAL_ERROR_CODES.includes(statusCode);
}

function App() {
  return (
    <React.StrictMode>
      <ErrorProvider>
          <AuthProvider msalConfig={msalConfig}>
          <QueryClientProvider client={queryClient}>
            <MasterTableProvider>
              <UserAccessControlProvider>
                <AppWithErrorHandler />
              </UserAccessControlProvider>
            </MasterTableProvider>
          </QueryClientProvider>
          </AuthProvider>
      </ErrorProvider>
    </React.StrictMode>
  );
}

export default App;